import React, { useRef, useState } from 'react';
import { CameraOutlined, CopyOutlined, DeleteOutlined, EyeOutlined, FolderOpenOutlined, LeftCircleOutlined, PlusOutlined, RightCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Card, Drawer, Flex, Image, List, Popconfirm, Space, Spin, Typography, Upload, theme } from 'antd';
import { DndContext, PointerSensor, useSensor } from '@dnd-kit/core';
import { SortableContext, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getPreviewSource } from './useComponent';

const TILE_SIZE = 84;
const { Text } = Typography;

const tileStyle = {
    flex: `0 0 ${TILE_SIZE}px`,
    width: `${TILE_SIZE}px`,
    height: `${TILE_SIZE}px`,
};

const ActionTile = ({ icon }) => (
    <Card hoverable size='small' style={{ width: '100%', height: '100%', overflow: 'hidden' }} styles={{ body: { padding: 0, height: '100%' } }}>
        <Flex align='center' justify='center' style={{ width: '100%', height: '100%' }}>
            <Button type='primary' shape='circle' size='middle' icon={icon} />
        </Flex>
    </Card>
);

const SortableImageCard = ({ file, viewModel, isActionOpen, onToggleActions }) => {
    const { token } = theme.useToken();
    const isUploading = !!file?._uploading;
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: file.uid,
        disabled: isUploading,
    });

    const style = {
        ...tileStyle,
        transform: CSS.Translate.toString(transform),
        transition,
        position: 'relative',
        touchAction: 'none',
        zIndex: isDragging ? 2 : 'auto',
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
            <Card
                size='small'
                styles={{ body: { padding: 0, height: '100%' } }}
                style={{
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden',
                    cursor: isUploading ? 'default' : isDragging ? 'grabbing' : 'grab',
                    borderColor: token.colorBorderSecondary,
                }}
            >
                <div
                    style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden', background: token.colorFillAlter }}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onToggleActions(file.uid);
                    }}
                >
                        <Image
                            preview={false}
                            wrapperStyle={{ width: '100%', height: '100%', display: 'block' }}
                            src={getPreviewSource(file)}
                            alt={file.name || 'image'}
                            fallback='data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=='
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                                opacity: isUploading ? 0.6 : 1,
                                filter: isUploading ? 'saturate(80%) blur(1px)' : 'none',
                                transition: 'opacity 0.2s ease, filter 0.2s ease',
                            }}
                        />

                        {isUploading ? (
                            <Flex
                                vertical
                                gap={6}
                                align='center'
                                justify='center'
                                style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.65)' }}
                            >
                                <Spin size='small' />
                                <Text strong style={{ fontSize: 11 }}>Đang upload...</Text>
                            </Flex>
                        ) : null}

                        {isActionOpen && !isUploading ? <Space size={6} style={{ position: 'absolute', top: 6, right: 6 }}>
                            <Button
                                type='primary'
                                shape='circle'
                                size='small'
                                icon={<EyeOutlined />}
                                onPointerDown={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                }}
                                onClick={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    viewModel.handlePreview(file);
                                }}
                            />

                            <Popconfirm
                                title='Xóa ảnh này?'
                                description='Ảnh sẽ bị gỡ khỏi danh sách hiện tại.'
                                onConfirm={() => viewModel.handleRemove(file)}
                                okText='Xóa'
                                cancelText='Hủy'
                            >
                                <Button
                                    danger
                                    shape='circle'
                                    size='small'
                                    icon={<DeleteOutlined />}
                                    onPointerDown={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        event.stopPropagation();
                                    }}
                                />
                            </Popconfirm>
                        </Space> : null}
                </div>
            </Card>
        </div>
    );
};

const MultiImagePickerMobile = ({ viewModel }) => {
    const [activeActionUid, setActiveActionUid] = useState(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const uploadRef = useRef(null);
    const sensor = useSensor(PointerSensor, {
        activationConstraint: {
            distance: 10,
        },
    });

    const triggerUpload = () => {
        setSheetOpen(false);
        setTimeout(() => {
            uploadRef.current?.querySelector('input[type="file"]')?.click();
        }, 300);
    };

    const triggerLibrary = () => {
        setSheetOpen(false);
        setTimeout(() => {
            viewModel.openLibrary();
        }, 300);
    };

    return (
        <Flex vertical gap={12} style={{ width: '100%' }}>
            <div ref={viewModel.panelRef} tabIndex={0} onClick={viewModel.handlePanelClick} onPaste={viewModel.handlePaste}>
                <Card size='small' styles={{ body: { padding: 12 } }} style={{ width: '100%' }}>
                    <Flex wrap gap={8} align='flex-start'>
                        <DndContext sensors={[sensor]} onDragEnd={viewModel.onDragEnd}>
                            <SortableContext items={viewModel.fileList.map((item) => item.uid)} strategy={horizontalListSortingStrategy}>
                                {viewModel.fileList.map((file) => (
                                    <SortableImageCard
                                        key={file.uid}
                                        file={file}
                                        viewModel={viewModel}
                                        isActionOpen={activeActionUid === file.uid}
                                        onToggleActions={(uid) => setActiveActionUid((currentUid) => (currentUid === uid ? null : uid))}
                                    />
                                ))}
                            </SortableContext>
                        </DndContext>

                        {viewModel.canAddMore ? (
                            <div
                                onClick={() => setSheetOpen(true)}
                                role='button'
                                tabIndex={0}
                                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSheetOpen(true)}
                                style={{ ...tileStyle, cursor: 'pointer' }}
                            >
                                <ActionTile icon={<PlusOutlined />} />
                            </div>
                        ) : null}

                        <div ref={uploadRef} style={{ display: 'none' }}>
                            <Upload showUploadList={false} multiple accept={viewModel.acceptValue} beforeUpload={viewModel.handleBeforeUpload}>
                                <Button>Upload</Button>
                            </Upload>
                        </div>
                    </Flex>
                </Card>
            </div>

            <Drawer
                placement='bottom'
                open={sheetOpen}
                onClose={() => setSheetOpen(false)}
                height='auto'
                title='Thêm ảnh'
                styles={{ body: { padding: '8px 0 16px' } }}
            >
                <List itemLayout='horizontal'>
                    <List.Item
                        onClick={triggerUpload}
                        style={{ padding: '12px 24px', cursor: 'pointer' }}
                    >
                        <List.Item.Meta
                            avatar={<UploadOutlined style={{ fontSize: 20 }} />}
                            title='Tải ảnh lên'
                            description='Chọn ảnh từ thiết bị của bạn'
                        />
                    </List.Item>
                    <List.Item
                        onClick={triggerLibrary}
                        style={{ padding: '12px 24px', cursor: 'pointer' }}
                    >
                        <List.Item.Meta
                            avatar={<FolderOpenOutlined style={{ fontSize: 20 }} />}
                            title='Chọn từ thư viện'
                            description='Chọn ảnh đã tải lên trước đó'
                        />
                    </List.Item>
                    <List.Item
                        onClick={viewModel.pasteImageFromClipboard}
                        style={{ padding: '12px 24px', cursor: 'pointer' }}
                    >
                        <List.Item.Meta
                            avatar={<CopyOutlined style={{ fontSize: 20 }} />}
                            title={viewModel.pasting ? 'Đang dán ảnh...' : 'Dán ảnh từ clipboard'}
                            description='Click để dán ảnh đã copy'
                        />
                    </List.Item>
                </List>
            </Drawer>

            {viewModel.previewImage ? (
                <>
                    <Image
                        wrapperStyle={{ display: 'none' }}
                        preview={{
                            visible: viewModel.previewOpen,
                            onVisibleChange: viewModel.handlePreviewVisibleChange,
                            afterOpenChange: (visible) => !visible && viewModel.setPreviewImage(''),
                        }}
                        src={viewModel.previewImage}
                    />

                    {viewModel.previewOpen && viewModel.fileList.length > 1 ? (
                        <Flex
                            justify='space-between'
                            align='center'
                            style={{
                                position: 'fixed',
                                inset: 0,
                                padding: '0 16px',
                                pointerEvents: 'none',
                                zIndex: 100000,
                            }}
                        >
                            <Button type='primary' shape='circle' size='middle' icon={<LeftCircleOutlined />} onClick={viewModel.handlePrev} style={{ pointerEvents: 'auto' }} />
                            <Button type='primary' shape='circle' size='middle' icon={<RightCircleOutlined />} onClick={viewModel.handleNext} style={{ pointerEvents: 'auto' }} />
                        </Flex>
                    ) : null}
                </>
            ) : null}
        </Flex>
    );
};

export default MultiImagePickerMobile;